import { useCallback, useEffect, useMemo, useState } from 'react';

import LinkOffIcon from '@mui/icons-material/LinkOff';
import MergeIcon from '@mui/icons-material/Merge';
import { Box, Button, Checkbox, Grid, Tooltip, useTheme } from '@mui/material';
import { Table, TableColumn } from 'components/Table';
import axios from 'axios';

import { GenericMergeDialog } from 'features/mergePage';

import strings from 'constants/index';
import { LocalityData } from 'features/localities';
import { usePaginatedRequest } from 'hooks/api';
import { useStoredTableState, useDialogStatus } from 'hooks/common';

const buttonGridStyles = {
  display: 'flex',
  marginLeft: '7px',
  alignItems: 'center',
};

interface DuplicateEntity {
  id: number;
  name: string;
  locality: LocalityData;
  [key: string]: any;
}

interface DuplicateGroup {
  key: string;
  duplicates: Array<DuplicateEntity>;
}

interface CompetitorsDuplicatesTableProps {
  endpoint: string;
  storageKey: string;
  columns: TableColumn[];
  localStorageKey: string;
  enableMerge?: boolean;
  mergeEndpoint?: string;
}

export const CompetitorsDuplicatesTable = ({
  endpoint,
  storageKey,
  columns: initialColumns,
  localStorageKey,
  enableMerge = false,
  mergeEndpoint = '/competitors/duplicates',
}: CompetitorsDuplicatesTableProps) => {
  const theme = useTheme();

  const [selectedRowsForMerge, setSelectedRowsForMerge] = useState<Set<string>>(new Set());
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(null);
  const {
    isOpen: isMergeDialogOpen,
    onShow: onShowMergeDialog,
    onHide: onHideMergeDialog,
  } = useDialogStatus();

  const {
    filters,
    page,
    pageSize,
    columns,
    setFilters: setStoredFilters,
    setOrders: setStoredSorts,
  } = useStoredTableState({
    columns: initialColumns,
    key: storageKey,
    disablePaginatedQueryParams: true,
  });

  const {
    data,
    setFilters: setPaginationFilters,
    setSortBy: setPaginationSort,
    setPageSize,
    setPage,
    refetch,
    isLoading,
  } = usePaginatedRequest<DuplicateGroup>({
    endpoint,
    storageKey: endpoint.slice(1, endpoint.length),
    initialParams: {
      page,
      pageSize,
      filters,
    },
  });

  const unlinkEntity = async (groupKey: string, entityId: number) => {
    await axios.post(`${endpoint}/unlink`, {
      entityId,
      groupKey,
    });
    refetch();
  };

  const processedEntities = useMemo(() => {
    const entities: any[] = [];
    data?.items?.forEach((group: DuplicateGroup, groupIndex: number) => {
      const isEven = groupIndex % 2 === 0;
      const backgroundColor = isEven ? theme.palette.action.hover : theme.palette.background.paper;
      group.duplicates.forEach((duplicate, duplicateIndex) => {
        entities.push({
          ...duplicate,
          groupKey: group.key,
          rowKey: `${group.key}:${duplicate.id}:${duplicateIndex}`,
          color: backgroundColor,
        });
      });
    });
    return entities;
  }, [data?.items, theme.palette.action.hover, theme.palette.background.paper]);

  useEffect(() => {
    if (!enableMerge) {
      return;
    }

    const visibleRowKeys = new Set(processedEntities.map(entity => entity.rowKey));

    setSelectedRowsForMerge(prev => {
      if (prev.size === 0) {
        return prev;
      }

      const prunedRowKeys = [...prev].filter(rowKey => visibleRowKeys.has(rowKey));
      if (prunedRowKeys.length === prev.size) {
        return prev;
      }

      if (prunedRowKeys.length === 0) {
        setSelectedGroupKey(null);
      } else {
        const firstSelected = processedEntities.find(entity => prunedRowKeys.includes(entity.rowKey));
        setSelectedGroupKey(firstSelected?.groupKey ?? null);
      }

      return new Set(prunedRowKeys);
    });
  }, [enableMerge, processedEntities]);

  const setFilters = useCallback(
    (filters: Array<PaginationFilter>) => {
      setStoredFilters(filters);
      setPaginationFilters(filters);
    },
    [setStoredFilters, setPaginationFilters],
  );

  const setSortBy = useCallback(
    (sorts: Array<PaginationSort>) => {
      setStoredSorts(sorts);
      setPaginationSort(sorts);
    },
    [setStoredSorts, setPaginationSort],
  );

  const handleMergeCheckboxChange = useCallback(
    (rowKey: string, groupKey: string, checked: boolean) => {
      setSelectedRowsForMerge(prev => {
        const newSet = new Set(prev);
        if (checked) {
          if (newSet.size === 0) {
            setSelectedGroupKey(groupKey);
            newSet.add(rowKey);
          } else if (newSet.size < 2 && selectedGroupKey === groupKey) {
            newSet.add(rowKey);
          }
        } else {
          newSet.delete(rowKey);
          if (newSet.size === 0) {
            setSelectedGroupKey(null);
          }
        }
        return newSet;
      });
    },
    [selectedGroupKey],
  );

  const handleMergeButtonClick = useCallback(() => {
    if (selectedRowsForMerge.size === 2) {
      onShowMergeDialog();
    }
  }, [selectedRowsForMerge.size, onShowMergeDialog]);

  const handleConfirmMergeDialog = useCallback(
    async (mergeData: { realId: number; fakeId: number }) => {
      await axios.post(mergeEndpoint, {
        originalId: mergeData.realId,
        duplicateId: mergeData.fakeId,
      });
      setSelectedRowsForMerge(new Set());
      setSelectedGroupKey(null);
      onHideMergeDialog();
    },
    [mergeEndpoint, onHideMergeDialog],
  );

  const isMergeButtonDisabled = selectedRowsForMerge.size !== 2;

  const getSelectedEntities = useCallback(() => {
    return processedEntities.filter(entity => selectedRowsForMerge.has(entity.rowKey));
  }, [selectedRowsForMerge, processedEntities]);

  const getEntityType = useCallback(() => {
    if (endpoint.includes('player-matches')) {
      return 'players' as const;
    } else if (endpoint.includes('team-matches')) {
      return 'teams' as const;
    } else if (endpoint.includes('doubles-matches')) {
      return 'doubles' as const;
    } else if (endpoint.includes('horse-matches')) {
      return 'horses' as const;
    } else if (endpoint.includes('venue-matches')) {
      return 'venues' as const;
    }
    return 'players' as const;
  }, [endpoint]);

  const unlinkTooltip = useMemo(() => {
    const tooltips = {
      players: strings.unlink_tooltip,
      teams: strings.unlink_tooltip_team,
      doubles: strings.unlink_tooltip,
      horses: strings.unlink_tooltip,
      venues: strings.unlink_tooltip_venue,
    } as const;

    return tooltips[getEntityType()];
  }, [endpoint, getEntityType]);

  const effectivePageSize = data?.pageSize ?? pageSize;
  const effectivePage = data?.page ?? page;

  return (
    <>
      {enableMerge && (
        <GenericMergeDialog
          open={isMergeDialogOpen}
          entities={getSelectedEntities()}
          entityType={getEntityType()}
          onClose={onHideMergeDialog}
          onConfirm={handleConfirmMergeDialog}
        />
      )}
      {enableMerge && (
        <Box sx={{ marginBottom: 2 }}>
          <Button
            variant="contained"
            disabled={isMergeButtonDisabled}
            onClick={handleMergeButtonClick}
            startIcon={<MergeIcon />}
          >
            Merge ({selectedRowsForMerge.size}/2)
          </Button>
        </Box>
      )}
      <Table
        columns={columns}
        loading={isLoading}
        localStorageKey={localStorageKey}
        onChangePage={(page: number) => setPage(page + 1)}
        onChangeRowsPerPage={setPageSize}
        onFilterBy={setFilters}
        onRequestSort={setSortBy}
        page={Math.max(0, effectivePage - 1)}
        rows={processedEntities.map(entity => {
          const isSelected = selectedRowsForMerge.has(entity.rowKey);
          const isFromSelectedGroup =
            selectedGroupKey === null || selectedGroupKey === entity.groupKey;
          const isDisabled =
            !isSelected && (!isFromSelectedGroup || selectedRowsForMerge.size >= 2);

          return {
            ...entity,
            actions: {
              value: (
                <Grid sx={buttonGridStyles}>
                  {enableMerge && (
                    <Grid item xs={4}>
                      <Tooltip placement="left" title="Select for merge (max 2 from same group)">
                        <Checkbox
                          checked={isSelected}
                          disabled={isDisabled}
                          onChange={e =>
                            handleMergeCheckboxChange(entity.rowKey, entity.groupKey, e.target.checked)
                          }
                          size="small"
                        />
                      </Tooltip>
                    </Grid>
                  )}
                  <Grid item xs={4}>
                    <Tooltip placement="left" title={unlinkTooltip}>
                      <LinkOffIcon
                        sx={{ cursor: 'pointer' }}
                        onClick={async () => await unlinkEntity(entity.groupKey, entity.id)}
                        fontSize="medium"
                      />
                    </Tooltip>
                  </Grid>
                </Grid>
              ),
            },
          };
        })}
        rowsCount={data?.totalItems ?? -1}
        rowsPerPage={effectivePageSize}
      />
    </>
  );
};
